/**
 * Service for agent configuration management API calls
 */

import api from './api';

export interface AgentConfiguration {
  id: number;
  config_name: string;
  config_type: 'tone' | 'behavior' | 'response_template';
  config_data: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentConfigurationCreate {
  config_name: string;
  config_type: 'tone' | 'behavior' | 'response_template';
  config_data: Record<string, any>;
  is_active?: boolean;
}

export interface AgentConfigurationUpdate {
  config_name?: string;
  config_type?: 'tone' | 'behavior' | 'response_template';
  config_data?: Record<string, any>;
  is_active?: boolean;
}

export interface ExampleConversation {
  id: number;
  conversation_name: string;
  user_message: string;
  expected_response: string;
  tone_category: string;
  created_at: string;
}

export interface ExampleConversationCreate {
  conversation_name: string;
  user_message: string;
  expected_response: string;
  tone_category: string;
}

export interface ExampleConversationUpdate {
  conversation_name?: string;
  user_message?: string;
  expected_response?: string;
  tone_category?: string;
}

export interface ConfigurationTestRequest {
  test_message: string;
  config_id?: number;
}

export interface ConfigurationTestResponse {
  test_message: string;
  generated_response: string;
  config_used: AgentConfiguration;
  timestamp: string;
}

export interface ConfigurationPreviewRequest {
  test_messages: string[];
  config_id?: number;
}

export interface ConfigurationPreviewResponse {
  config_used: AgentConfiguration;
  responses: Array<{
    test_message: string;
    generated_response: string;
  }>;
  timestamp: string;
}

export interface ABTestRequest {
  config_a_id: number;
  config_b_id: number;
  test_messages: string[];
}

export interface ABTestResponse {
  config_a: AgentConfiguration;
  config_b: AgentConfiguration;
  responses_a: Array<{
    test_message: string;
    generated_response: string;
  }>;
  responses_b: Array<{
    test_message: string;
    generated_response: string;
  }>;
  timestamp: string;
}

export interface ConfigurationDeploymentRequest {
  config_id: number;
  deployment_notes?: string;
}

export interface ConfigurationDeploymentResponse {
  config_deployed: AgentConfiguration;
  previous_active_config?: AgentConfiguration;
  deployment_notes?: string;
  deployed_at: string;
  deployed_by: string;
}

export interface GlobalCompanyContext {
  id: number;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

export interface GlobalCompanyContextUpsertRequest {
  content: string;
  is_active?: boolean;
  updated_by?: string;
}

export const agentConfigService = {
  async getGlobalCompanyContext(): Promise<GlobalCompanyContext | null> {
    const response = await api.get('/agent-config/global-company-context');
    return response.data;
  },

  async upsertGlobalCompanyContext(
    data: GlobalCompanyContextUpsertRequest
  ): Promise<GlobalCompanyContext> {
    const response = await api.put('/agent-config/global-company-context', data);
    return response.data;
  },

  // Agent Configuration methods
  async getConfigurations(params?: {
    skip?: number;
    limit?: number;
    config_type?: string;
    is_active?: boolean;
  }): Promise<AgentConfiguration[]> {
    console.log('🌐 API Call: GET /agent-config/configurations', { params });
    const response = await api.get('/agent-config/configurations', { params });
    console.log('📡 API Response:', response.data);
    return response.data;
  },

  async getActiveConfiguration(config_type?: string): Promise<AgentConfiguration | null> {
    const response = await api.get('/agent-config/configurations/active', {
      params: config_type ? { config_type } : undefined
    });
    return response.data;
  },

  async getConfiguration(id: number): Promise<AgentConfiguration> {
    const response = await api.get(`/agent-config/configurations/${id}`);
    return response.data;
  },

  async createConfiguration(data: AgentConfigurationCreate): Promise<AgentConfiguration> {
    const response = await api.post('/agent-config/configurations', data);
    return response.data;
  },

  async updateConfiguration(id: number, data: AgentConfigurationUpdate): Promise<AgentConfiguration> {
    const response = await api.put(`/agent-config/configurations/${id}`, data);
    return response.data;
  },

  async deleteConfiguration(id: number): Promise<void> {
    await api.delete(`/agent-config/configurations/${id}`);
  },

  async activateConfiguration(id: number): Promise<AgentConfiguration> {
    const response = await api.post(`/agent-config/configurations/${id}/activate`);
    return response.data;
  },

  async testConfiguration(data: ConfigurationTestRequest): Promise<ConfigurationTestResponse> {
    const response = await api.post('/agent-config/configurations/test', data);
    return response.data;
  },

  async previewConfiguration(data: ConfigurationPreviewRequest): Promise<ConfigurationPreviewResponse> {
    const response = await api.post('/agent-config/configurations/preview', data);
    return response.data;
  },

  async abTestConfigurations(data: ABTestRequest): Promise<ABTestResponse> {
    const response = await api.post('/agent-config/configurations/ab-test', data);
    return response.data;
  },

  async deployConfiguration(data: ConfigurationDeploymentRequest): Promise<ConfigurationDeploymentResponse> {
    const response = await api.post(`/agent-config/configurations/${data.config_id}/deploy`, data);
    return response.data;
  },

  // Example Conversation methods
  async getExampleConversations(params?: {
    skip?: number;
    limit?: number;
    tone_category?: string;
  }): Promise<ExampleConversation[]> {
    console.log('🌐 API Call: GET /agent-config/example-conversations', { params });
    const response = await api.get('/agent-config/example-conversations', { params });
    console.log('📡 API Response:', response.data);
    return response.data;
  },

  async searchExampleConversations(query: string, params?: {
    skip?: number;
    limit?: number;
  }): Promise<ExampleConversation[]> {
    const response = await api.get('/agent-config/example-conversations/search', {
      params: { q: query, ...params }
    });
    return response.data;
  },

  async getConversationsByTone(tone_category: string): Promise<ExampleConversation[]> {
    const response = await api.get(`/agent-config/example-conversations/by-tone/${tone_category}`);
    return response.data;
  },

  async getExampleConversation(id: number): Promise<ExampleConversation> {
    const response = await api.get(`/agent-config/example-conversations/${id}`);
    return response.data;
  },

  async createExampleConversation(data: ExampleConversationCreate): Promise<ExampleConversation> {
    const response = await api.post('/agent-config/example-conversations', data);
    return response.data;
  },

  async updateExampleConversation(id: number, data: ExampleConversationUpdate): Promise<ExampleConversation> {
    const response = await api.put(`/agent-config/example-conversations/${id}`, data);
    return response.data;
  },

  async deleteExampleConversation(id: number): Promise<void> {
    await api.delete(`/agent-config/example-conversations/${id}`);
  }
};
